import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserState } from './UserState';
import { useSubscription } from '@meraki-internal/state';
import { useEffect, useRef, useState } from 'react';
import { Modal } from '../components/Modal';
import { AppPrincipal, isCaremapViewer } from '../auth/AppPrincipal';
import { IonButton, IonContent, isPlatform } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { Text } from '../components/Text';
import { TextField } from '../components/TextField';

const useStyles = createUseStyles({
    continueBtn: {
        marginBottom: 16,
        paddingLeft: 70,
        paddingRight: 70
    },

    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 26,
        marginTop: -20,
        paddingLeft: 50,
        paddingRight: 50,
        paddingTop:16
    }
});

export const RequireNameModal: React.FC = () => {
    const classes = useStyles();

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const principal = useInstance(AppPrincipal);
    const isViewer = isCaremapViewer(principal);

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        Promise.resolve()
            .then(async () => {
                // wait to render until we give components a chance to redirect
                await new Promise(resolve => setTimeout(resolve, 250));
                setIsOpen(!isViewer && !Boolean(userState.getName()));
            });
    }, [userState, principal, isViewer]);

    const firstNameFieldRef = useRef<HTMLIonInputElement>(null);
    useEffect(() => {
        if (isPlatform('desktop') || isPlatform('capacitor')) {
            const timer = setTimeout(() => {
                if (firstNameFieldRef.current && !userState.state.firstName) {
                    firstNameFieldRef.current.setFocus();
                }
            }, 350);
            return () => clearTimeout(timer);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const hasValidName = Boolean(userState.state.firstName);

    const saveAndClose = async () => {
        if (userState.isDirty){
            await userState.save();
            setIsOpen(false);
        }
        // this shouldn't happen, but if they close it and we have a name, and there is nothing to save
        // then close it
        else if (hasValidName){
            setIsOpen(false);
        }
    };

    // NOTE: I ran into issues where an empty modal would be displayed
    // this appears to be due to internal of ion-modal (eg https://github.com/ionic-team/ionic-framework/issues/25775)
    // my work around was to delay rendering the modal
    return (
        <Modal
            desktopMaxHeight={360}
            data-id="require-name-modal"
            title="Please Enter Your Name"
            centerTitle
            isOpen={isOpen}
            hideCloseButton
            preventBackdropDismiss
            // closeHandler not used b/c hideCloseButton & preventBackdropDismiss both true
            closeHandler={() => {}}
        >
            <IonContent className="ion-padding">
                <div className={classes.contentWrapper}>
                    <div>
                        <Text>
                            Help your family and friends recognize you within the Innerhive community!
                        </Text>
                    </div>
                    <TextField
                        inputRef={firstNameFieldRef}
                        label="First Name"
                        name="firstname"
                        value={userState.state.firstName}
                        autocapitalize="words"
                        onInput={name => userState.setFirstName(name)}
                    />
                    <TextField
                        label="Last Name (optional)"
                        value={userState.state.lastName}
                        name="lastname"
                        autocapitalize="words"
                        onInput={name => userState.setLastName(name)}
                    />
                    <IonButton
                        data-id="continue-button"
                        className={classes.continueBtn}
                        disabled={!userState.state.firstName}
                        onClick={saveAndClose}
                    >
                        Continue
                    </IonButton>
                </div>
            </IonContent>
        </Modal>
    );
};
